import styled from "styled-components";
import {
  TTableBody,
  TRow,
  TCellStyleVariants,
  TSelection,
  TCell,
  TCellButton,
  TFullData,
  TSecondRow,
  TText,
  TSkeletonConfig,
} from ".";
import Check from "../Inputs/Check";
import Radio from "../Inputs/Radio";
import { CSSProperties, Fragment, ReactNode, useEffect, useState } from "react";
import Button, { TWithIconVariants } from "../Button";
import Tag from "../Tag";
import DropdownOnlyIcon from "../DropdownOnlyIcon";
import React from "react";
import Skeleton from "../Skeleton";
import Tooltip from "../Tooltip";
import { Property } from "csstype";

const Container = styled.tbody<{
  $row: TRow;
  $hasFooter: "hasFooter" | "noFooter";
  $gridTemplateColumns: string;
}>`
  font-size: 14px;
  font-family: var(--font-paragraph-default);
  color: var(--color-text-paragraph);

  display: block;
  overflow: auto;

  tr {
    display: grid;
    grid-template-columns: ${({ $gridTemplateColumns }) =>
      $gridTemplateColumns};
    align-items: center;
    height: 56px;
    ${({ $row }) =>
      $row === "singleRow" &&
      `border-bottom: 1px solid var(--color-light-200);`}
  }
  span {
    color: var(--color-text-labels);
  }

  tr.custom-second-row {
    height: 40px;
  }

  ${({ $row }) =>
    ["doubleRow", "tripleRow"].includes($row) &&
    `tr.second-row {
    height: 40px;
    background: var(--color-light-100);
    display: flex;
    justify-content: space-between;
    border-bottom: 1px solid var(--color-light-200);  
    td {
      display: flex;
      align-items: center;
      height: 100%;
      & button {
        color: var(--color-main-blue);
        cursor: pointer;
        svg {
          width: 14px;
          height: 14px;
        }
      }
      &.link-container {
        span, button {
          display: flex;
          align-items: center;
        }
        span {
          margin-right: 12px;
        }
      }
    }
    p {
      margin-left: 15px;
      margin-right: 3px;
      font-size: 12px;
    }
    span {
      font-size: 12px;
    }
  }`}
`;

type TTBodyProps = {
  tableBody: {
    elementId: string;
    file_xml: null | string;
    file_nfe: null | string;
    file_boleto?: string;
    rowTooltip?: {
      size?: "default" | "large";
      text: TText[];
      customTextAlignment?: Property.TextAlign;
      customTopPosition?: number;
    };
    body: (
      | TTableBody<TCellStyleVariants>
      | (NonNullable<ReactNode> & {
          skeleton?: {
            containerCss?: CSSProperties;
            values: TSkeletonConfig[];
          };
        })
    )[];
  }[];
  selectType: TFullData<TSelection>;
  gridTemplateColumns: string;
  borderColor?: string[];
  row: TRow;
  hasFooter: "hasFooter" | "noFooter";
  secondRow?: TSecondRow;
  isLoading?: boolean;
  thirdRow?: TSecondRow;
  forceSelectElementRef?: React.MutableRefObject<
    {
      elementId: string;
      isClicked: "" | "active" | "inactive";
      setSelectElement: React.Dispatch<
        React.SetStateAction<"" | "active" | "inactive">
      >;
    }[]
  >;
  onRowSelect: (selected: {
    isChecked: boolean;
    clickedElementId: string;
    setClickState?: React.Dispatch<
      React.SetStateAction<"" | "active" | "inactive">
    >;
  }) => void;
  setSelectedRows?: number[];
  tbodyRef?: React.MutableRefObject<HTMLTableSectionElement | null>;
};

const TBody: React.FC<TTBodyProps> = ({
  tableBody,
  selectType,
  borderColor,
  row,
  secondRow,
  thirdRow,
  onRowSelect,
  forceSelectElementRef,
  hasFooter,
  gridTemplateColumns,
  isLoading,
  tbodyRef,
}) => {
  const [shouldUpdateElement /* setShouldUpdateElement */] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      if (selectType.type === "singleSelect" && tableBody.length > 0) {
        if (onRowSelect) {
          onRowSelect({
            isChecked: true,
            clickedElementId: tableBody[0].elementId,
          });
        }
      }
    });
  }, [selectType.type]);

  const onClickHandler = (
    isClicked: boolean,
    clickedElementId: string,
    setClickState?: React.Dispatch<
      React.SetStateAction<"" | "active" | "inactive">
    >
  ) => {
    if (selectType.type === "singleSelect" && isClicked) {
      if (forceSelectElementRef?.current) {
        forceSelectElementRef.current.forEach((element) => {
          if (element.elementId !== clickedElementId) {
            element.setSelectElement("inactive");
            element.isClicked = "inactive";
          }
        });
      }
    }
    onRowSelect({
      isChecked: isClicked,
      clickedElementId: clickedElementId,
      setClickState: setClickState,
    });
  };

  const handleTableDataStyle = (
    cell:
      | TCell<Exclude<TCellStyleVariants, "button">>
      | TCellButton<TWithIconVariants>
      | ReactNode
  ) => {
    if (cell && typeof cell === "object" && "style" in cell) {
      switch (cell?.style) {
        case "bold":
          return cell.skeleton ? (
            <div style={cell.skeleton.containerCss}>
              {cell.skeleton.values.map((skeletonProps, idx) => (
                <Skeleton key={idx} {...skeletonProps} />
              ))}
            </div>
          ) : (
            <>
              <b
                style={{
                  marginRight: cell.marginRight,
                  fontSize: cell.fontSize,
                }}
              >
                {cell.value}
              </b>
              {cell.shouldBreakLine && <br />}
            </>
          );
        case "default":
          return cell.skeleton ? (
            <div style={cell.skeleton.containerCss}>
              {cell.skeleton.values.map((skeletonProps, idx) => (
                <Skeleton key={idx} {...skeletonProps} />
              ))}
            </div>
          ) : (
            <>
              <p
                style={{
                  display: "inline",
                  marginRight: cell.marginRight,
                  fontSize: cell.fontSize,
                }}
              >
                {cell.value}
              </p>
              {cell.shouldBreakLine && <br />}
            </>
          );
        case "link":
          return cell.skeleton ? (
            <div style={cell.skeleton.containerCss}>
              {cell.skeleton.values.map((skeletonProps, idx) => (
                <Skeleton key={idx} {...skeletonProps} />
              ))}
            </div>
          ) : (
            <>
              <a
                href={typeof cell.url === "string" ? cell.url : cell.url?.href}
                target={
                  typeof cell.url === "string"
                    ? "_blank"
                    : cell.url?.isExternal
                    ? "_blank"
                    : "_self"
                }
                rel={
                  typeof cell.url === "string"
                    ? "noopener noreferrer"
                    : cell.url?.isExternal
                    ? "noopener noreferrer"
                    : undefined
                }
                style={{
                  display: "inline",
                  marginRight: cell.marginRight,
                  fontSize: cell.fontSize ?? "14px",
                  color: "var(--color-text-link)",
                }}
              >
                {cell.value}
              </a>
              {cell.shouldBreakLine && <br />}
            </>
          );
        case "faded":
          return cell.skeleton ? (
            <div style={cell.skeleton.containerCss}>
              {cell.skeleton.values.map((skeletonProps, idx) => (
                <Skeleton key={idx} {...skeletonProps} />
              ))}
            </div>
          ) : (
            <>
              <span
                style={{
                  marginRight: cell.marginRight,
                  fontSize: cell.fontSize,
                }}
              >
                {cell.value}
              </span>
              {cell.shouldBreakLine && <br />}
            </>
          );
        case "alert":
          return cell.skeleton ? (
            <div style={cell.skeleton.containerCss}>
              {cell.skeleton.values.map((skeletonProps, idx) => (
                <Skeleton key={idx} {...skeletonProps} />
              ))}
            </div>
          ) : (
            <>
              <span
                style={{
                  color: "var(--color-aux-danger-700)",
                  marginRight: cell.marginRight,
                  fontSize: cell.fontSize,
                }}
              >
                {cell.value}
              </span>
              {cell.shouldBreakLine && <br />}
            </>
          );
        case "miscellaneous":
          return cell.skeleton ? (
            <div style={cell.skeleton.containerCss}>
              {cell.skeleton.values.map((skeletonProps, idx) => (
                <Skeleton key={idx} {...skeletonProps} />
              ))}
            </div>
          ) : cell.tooltip ? (
            <Tooltip
              justifyContent="flex-start"
              text={cell.tooltip.text}
            >
              <>
                {cell.values.map((value, index) => {
                  switch (value.style) {
                    case "bold":
                      return (
                        <React.Fragment key={index}>
                          <b
                            style={{
                              marginRight: value.marginRight,
                              fontSize: value.fontSize,
                            }}
                          >
                            {value.text}
                          </b>
                          {value.shouldBreakLine && <br />}
                        </React.Fragment>
                      );
                    case "default":
                      return (
                        <React.Fragment key={index}>
                          <p
                            style={{
                              display: "inline",
                              marginRight: value.marginRight,
                              fontSize: value.fontSize,
                            }}
                          >
                            {value.text}
                          </p>
                          {value.shouldBreakLine && <br />}
                        </React.Fragment>
                      );
                    case "faded":
                      return (
                        <React.Fragment key={index}>
                          <span
                            style={{
                              marginRight: value.marginRight,
                              fontSize: value.fontSize,
                            }}
                          >
                            {value.text}
                          </span>
                          {value.shouldBreakLine && <br />}
                        </React.Fragment>
                      );
                    case "alert":
                      return (
                        <React.Fragment key={index}>
                          <span
                            style={{
                              color: "var(--color-aux-danger-700)",
                              marginRight: value.marginRight,
                              fontSize: value.fontSize,
                            }}
                          >
                            {value.text}
                          </span>
                          {value.shouldBreakLine && <br />}
                        </React.Fragment>
                      );
                  }
                })}
              </>
            </Tooltip>
          ) : (
            cell.values.map((value, index) => {
              switch (value.style) {
                case "bold":
                  return (
                    <React.Fragment key={index}>
                      <b
                        style={{
                          marginRight: value.marginRight,
                          fontSize: value.fontSize,
                        }}
                      >
                        {value.text}
                      </b>
                      {value.shouldBreakLine && <br />}
                    </React.Fragment>
                  );
                case "default":
                  return (
                    <React.Fragment key={index}>
                      <p
                        style={{
                          display: "inline",
                          marginRight: value.marginRight,
                          fontSize: value.fontSize,
                        }}
                      >
                        {value.text}
                      </p>
                      {value.shouldBreakLine && <br />}
                    </React.Fragment>
                  );
                case "faded":
                  return (
                    <React.Fragment key={index}>
                      <span
                        style={{
                          marginRight: value.marginRight,
                          fontSize: value.fontSize,
                        }}
                      >
                        {value.text}
                      </span>
                      {value.shouldBreakLine && <br />}
                    </React.Fragment>
                  );
                case "alert":
                  return (
                    <React.Fragment key={index}>
                      <span
                        style={{
                          color: "var(--color-aux-danger-700)",
                          marginRight: value.marginRight,
                          fontSize: value.fontSize,
                        }}
                      >
                        {value.text}
                      </span>
                      {value.shouldBreakLine && <br />}
                    </React.Fragment>
                  );
              }
            })
          );
        case "button":
          switch (cell.icon) {
            case "ellipsisV":
              return (
                <DropdownOnlyIcon
                  key={`${cell.isDisabled}`}
                  content={cell.content}
                  onMenuSelect={cell.onMenuSelect}
                  onClick={cell.onClick}
                  isDisabled={cell.isDisabled}
                  scrollableRef={cell.hasScrollableRef ? tbodyRef : undefined}
                />
              );
            case "arrowRight":
              return (
                <Button
                  variant="secondary"
                  icon="onlyIcon"
                  id={cell.id}
                  iconType={cell.icon}
                  onClick={cell.onClick}
                  disabled={cell.isDisabled}
                  style={cell.customCSS}
                  tooltip={
                    cell.tooltip && {
                      text: cell.tooltip.text,
                      customTextAlignment: cell.tooltip.customTextAlignment,
                      size: cell.tooltip.size,
                      scrollableRef: cell.tooltip.hasScrollableRef
                        ? tbodyRef
                        : undefined,
                    }
                  }
                />
              );
            case "times-red":
              return (
                <Button
                  variant="danger"
                  icon="onlyIcon"
                  id={cell.id}
                  iconType={cell.icon}
                  onClick={cell.onClick}
                  disabled={cell.isDisabled}
                  style={cell.customCSS}
                  tooltip={
                    cell.tooltip && {
                      text: cell.tooltip.text,
                      customTextAlignment: cell.tooltip.customTextAlignment,
                      size: cell.tooltip.size,
                      scrollableRef: cell.tooltip.hasScrollableRef
                        ? tbodyRef
                        : undefined,
                    }
                  }
                />
              );
            default:
              return (
                <Button
                  variant="secondary"
                  icon="onlyIcon"
                  id={cell.id}
                  data-testid={cell["data-testid"]}
                  iconType={cell.icon}
                  onClick={cell.onClick}
                  disabled={cell.isDisabled}
                  tooltip={
                    cell.tooltip && {
                      text: cell.tooltip.text,
                      size: cell.tooltip.size,
                      customTextAlignment: cell.tooltip.customTextAlignment,
                      scrollableRef: cell.tooltip.hasScrollableRef
                        ? tbodyRef
                        : undefined,
                    }
                  }
                />
              );
          }
        case "gray":
          return cell.icon === "noIcon" ? (
            <Tag
              icon={cell.icon}
              size="medium"
              color={cell.style}
              text={cell.value}
            />
          ) : (
            <Tag
              icon={cell.icon}
              iconPosition={cell.iconPosition}
              size="medium"
              color={cell.style}
              text={cell.value}
            />
          );
        case "orange":
          return cell.icon === "noIcon" ? (
            <Tag
              icon={cell.icon}
              size="medium"
              color={cell.style}
              text={cell.value}
            />
          ) : (
            <Tag
              icon={cell.icon}
              iconPosition={cell.iconPosition}
              size="medium"
              color={cell.style}
              text={cell.value}
            />
          );
        case "green":
          return cell.icon === "noIcon" ? (
            <Tag
              icon={cell.icon}
              size="medium"
              color={cell.style}
              text={cell.value}
            />
          ) : (
            <Tag
              icon={cell.icon}
              iconPosition={cell.iconPosition}
              size="medium"
              color={cell.style}
              text={cell.value}
            />
          );
        case "red":
          return cell.icon === "noIcon" ? (
            <Tag
              icon={cell.icon}
              size="medium"
              color={cell.style}
              text={cell.value}
            />
          ) : (
            <Tag
              icon={cell.icon}
              iconPosition={cell.iconPosition}
              size="medium"
              color={cell.style}
              text={cell.value}
            />
          );
        case "purple":
          return cell.icon === "noIcon" ? (
            <Tag
              icon={cell.icon}
              size="medium"
              color={cell.style}
              text={cell.value}
            />
          ) : (
            <Tag
              icon={cell.icon}
              iconPosition={cell.iconPosition}
              size="medium"
              color={cell.style}
              text={cell.value}
            />
          );
        case "rose":
          return cell.icon === "noIcon" ? (
            <Tag
              icon={cell.icon}
              size="medium"
              color={cell.style}
              text={cell.value}
            />
          ) : (
            <Tag
              icon={cell.icon}
              iconPosition={cell.iconPosition}
              size="medium"
              color={cell.style}
              text={cell.value}
            />
          );
        case "blue":
          return cell.icon === "noIcon" ? (
            <Tag
              icon={cell.icon}
              size="medium"
              color={cell.style}
              text={cell.value}
            />
          ) : (
            <Tag
              icon={cell.icon}
              iconPosition={cell.iconPosition}
              size="medium"
              color={cell.style}
              text={cell.value}
            />
          );
        case "darkBlue":
          return cell.icon === "noIcon" ? (
            <Tag
              icon={cell.icon}
              size="medium"
              color={cell.style}
              text={cell.value}
            />
          ) : (
            <Tag
              icon={cell.icon}
              iconPosition={cell.iconPosition}
              size="medium"
              color={cell.style}
              text={cell.value}
            />
          );
        case "darkGray":
          return cell.icon === "noIcon" ? (
            <Tag
              icon={cell.icon}
              size="medium"
              color={cell.style}
              text={cell.value}
            />
          ) : (
            <Tag
              icon={cell.icon}
              iconPosition={cell.iconPosition}
              size="medium"
              color={cell.style}
              text={cell.value}
            />
          );
        case "brokenLink":
          return cell.icon === "noIcon" ? (
            <Tag
              icon={cell.icon}
              size="medium"
              color={cell.style}
              text={cell.value}
            />
          ) : (
            <Tag
              icon={cell.icon}
              iconPosition={cell.iconPosition}
              size="medium"
              color={cell.style}
              text={cell.value}
            />
          );
      }
    } else {
      return cell;
    }
  };

  return (
    <Container
      ref={tbodyRef}
      $row={row}
      $hasFooter={hasFooter}
      $gridTemplateColumns={gridTemplateColumns}
    >
      {tableBody.map((bodyData, rowIndex) => {
        return (
          <Fragment key={rowIndex}>
            {bodyData.rowTooltip ? (
              <Tooltip {...bodyData.rowTooltip}>
                <tr
                  style={{
                    height: borderColor && "96px",
                    background:
                      borderColor && borderColor[rowIndex]?.includes("red")
                        ? "var(--color-aux-danger-100)"
                        : "var(--color-light-0)",
                    borderBottom:
                      borderColor &&
                      (borderColor[rowIndex]?.includes("red")
                        ? rowIndex !== tableBody.length - 1
                          ? "1px solid var(--color-aux-danger-700)"
                          : "none"
                        : borderColor[rowIndex]?.includes("light")
                        ? rowIndex !== tableBody.length - 1
                          ? "1px solid var(--color-light-200)"
                          : "none"
                        : "none"),
                  }}
                >
                  <td></td>
                  <td>
                    {selectType.type === "multiSelect" ? (
                      isLoading ? (
                        <Skeleton variant="square" width={18} height={18} />
                      ) : (
                        <>
                          <Check
                            data-testid={"row-checkbox"}
                            key={bodyData.elementId + shouldUpdateElement}
                            setSelectedRef={(setClickState, clickState) => {
                              if (forceSelectElementRef?.current) {
                                const repeatedElementIndex =
                                  forceSelectElementRef.current.findIndex(
                                    (element) =>
                                      element.elementId === bodyData.elementId
                                  );
                                if (repeatedElementIndex > -1) {
                                  forceSelectElementRef.current.splice(
                                    repeatedElementIndex,
                                    1
                                  );
                                }
                                forceSelectElementRef.current.push({
                                  elementId: bodyData.elementId,
                                  isClicked: clickState,
                                  setSelectElement: setClickState,
                                });
                              }
                            }}
                            onClick={(isClicked, setClickState) => {
                              onClickHandler(
                                isClicked,
                                `${bodyData.elementId}`,
                                setClickState
                              );
                            }}
                          />
                          &nbsp;
                        </>
                      )
                    ) : selectType.type === "singleSelect" ? (
                      !Array.isArray(bodyData.body[0]) &&
                      bodyData.body[0].skeleton ? (
                        <div style={bodyData.body[0].skeleton.containerCss}>
                          {bodyData.body[0].skeleton.values.map(
                            (skeletonProps, idx) => (
                              <Skeleton key={idx} {...skeletonProps} />
                            )
                          )}
                        </div>
                      ) : (
                        <>
                          <Radio
                            key={bodyData.elementId + shouldUpdateElement}
                            setSelectedRef={(setClickState, clickState) => {
                              if (forceSelectElementRef?.current) {
                                const repeatedElementIndex =
                                  forceSelectElementRef.current.findIndex(
                                    (element) =>
                                      element.elementId === bodyData.elementId
                                  );
                                if (repeatedElementIndex > -1) {
                                  forceSelectElementRef.current.splice(
                                    repeatedElementIndex,
                                    1
                                  );
                                }

                                forceSelectElementRef.current.push({
                                  elementId: bodyData.elementId,
                                  isClicked: clickState,
                                  setSelectElement: setClickState,
                                });
                              }
                            }}
                            onClick={(isClicked, setClickState) => {
                              onClickHandler(
                                isClicked,
                                `${bodyData.elementId}`,
                                setClickState
                              );
                            }}
                          />
                          &nbsp;
                        </>
                      )
                    ) : (
                      <>{""}&nbsp;</>
                    )}
                  </td>
                  {bodyData.body.map((cell, index) => (
                    <td key={index}>
                      {!Array.isArray(cell) && handleTableDataStyle(cell) ? (
                        handleTableDataStyle(cell)
                      ) : (
                        <div style={{ width: 150 }}></div>
                      )}
                      {Array.isArray(cell) && (
                        <div
                          style={
                            (cell as any).forceFlex
                              ? cell.some(
                                  (
                                    value:
                                      | TCell<
                                          Exclude<TCellStyleVariants, "button">
                                        >
                                      | TCellButton<TWithIconVariants>
                                  ) =>
                                    typeof value === "object" &&
                                    value !== null &&
                                    "shouldBreakLine" in value &&
                                    value.shouldBreakLine
                                )
                                ? // If any value has shouldBreakLine, use column to allow <br /> to work
                                  { display: "flex", flexDirection: "column" }
                                : { display: "flex", alignItems: "center" }
                              : cell.some(
                                  (
                                    value:
                                      | TCell<
                                          Exclude<TCellStyleVariants, "button">
                                        >
                                      | TCellButton<TWithIconVariants>
                                  ) =>
                                    typeof value === "object" &&
                                    value !== null &&
                                    "shouldBreakLine" in value &&
                                    value.shouldBreakLine
                                )
                              ? undefined // default block, so <br /> works
                              : { display: "flex", alignItems: "center" }
                          }
                        >
                          {cell.map((value, index) => (
                            <React.Fragment key={index}>
                              {handleTableDataStyle(value)}
                            </React.Fragment>
                          ))}
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
              </Tooltip>
            ) : (
              <tr
                style={{
                  height: borderColor && "96px",
                  background:
                    borderColor && borderColor[rowIndex]?.includes("red")
                      ? "var(--color-aux-danger-100)"
                      : "var(--color-light-0)",
                  borderBottom:
                    borderColor &&
                    (borderColor[rowIndex]?.includes("red")
                      ? rowIndex !== tableBody.length - 1
                        ? "1px solid var(--color-aux-danger-700)"
                        : "none"
                      : borderColor[rowIndex]?.includes("light")
                      ? rowIndex !== tableBody.length - 1
                        ? "1px solid var(--color-light-200)"
                        : "none"
                      : "none"),
                }}
              >
                <td></td>
                <td>
                  {selectType.type === "multiSelect" ? (
                    isLoading ? (
                      <Skeleton variant="square" width={18} height={18} />
                    ) : (
                      <>
                        <Check
                          data-testid={"row-checkbox"}
                          key={bodyData.elementId + shouldUpdateElement}
                          setSelectedRef={(setClickState, clickState) => {
                            if (forceSelectElementRef?.current) {
                              const repeatedElementIndex =
                                forceSelectElementRef.current.findIndex(
                                  (element) =>
                                    element.elementId === bodyData.elementId
                                );
                              if (repeatedElementIndex > -1) {
                                forceSelectElementRef.current.splice(
                                  repeatedElementIndex,
                                  1
                                );
                              }
                              forceSelectElementRef.current.push({
                                elementId: bodyData.elementId,
                                isClicked: clickState,
                                setSelectElement: setClickState,
                              });
                            }
                          }}
                          onClick={(isClicked, setClickState) => {
                            onClickHandler(
                              isClicked,
                              `${bodyData.elementId}`,
                              setClickState
                            );
                          }}
                        />
                        &nbsp;
                      </>
                    )
                  ) : selectType.type === "singleSelect" ? (
                    !Array.isArray(bodyData.body[0]) &&
                    bodyData.body[0].skeleton ? (
                      <div style={bodyData.body[0].skeleton.containerCss}>
                        {bodyData.body[0].skeleton.values.map(
                          (skeletonProps, idx) => (
                            <Skeleton key={idx} {...skeletonProps} />
                          )
                        )}
                      </div>
                    ) : (
                      <>
                        <Radio
                          key={bodyData.elementId + shouldUpdateElement}
                          setSelectedRef={(setClickState, clickState) => {
                            if (forceSelectElementRef?.current) {
                              const repeatedElementIndex =
                                forceSelectElementRef.current.findIndex(
                                  (element) =>
                                    element.elementId === bodyData.elementId
                                );
                              if (repeatedElementIndex > -1) {
                                forceSelectElementRef.current.splice(
                                  repeatedElementIndex,
                                  1
                                );
                              }

                              forceSelectElementRef.current.push({
                                elementId: bodyData.elementId,
                                isClicked: clickState,
                                setSelectElement: setClickState,
                              });
                            }
                          }}
                          onClick={(isClicked, setClickState) => {
                            onClickHandler(
                              isClicked,
                              `${bodyData.elementId}`,
                              setClickState
                            );
                          }}
                        />
                        &nbsp;
                      </>
                    )
                  ) : (
                    <>{""}&nbsp;</>
                  )}
                </td>
                {bodyData.body.map((cell, index) => (
                  <td key={index}>
                    {!Array.isArray(cell) && handleTableDataStyle(cell) ? (
                      handleTableDataStyle(cell)
                    ) : (
                      <div style={{ width: 150 }}></div>
                    )}
                    {Array.isArray(cell) && (
                      <div
                        style={
                          (cell as any).forceFlex
                            ? cell.some(
                                (
                                  value:
                                    | TCell<
                                        Exclude<TCellStyleVariants, "button">
                                      >
                                    | TCellButton<TWithIconVariants>
                                ) =>
                                  typeof value === "object" &&
                                  value !== null &&
                                  "shouldBreakLine" in value &&
                                  value.shouldBreakLine
                              )
                              ? // If any value has shouldBreakLine, use column to allow <br /> to work
                                { display: "flex", flexDirection: "column" }
                              : { display: "flex", alignItems: "center" }
                            : cell.some(
                                (
                                  value:
                                    | TCell<
                                        Exclude<TCellStyleVariants, "button">
                                      >
                                    | TCellButton<TWithIconVariants>
                                ) =>
                                  typeof value === "object" &&
                                  value !== null &&
                                  "shouldBreakLine" in value &&
                                  value.shouldBreakLine
                              )
                            ? undefined // default block, so <br /> works
                            : { display: "flex", alignItems: "center" }
                        }
                      >
                        {cell.map((value, index) => (
                          <React.Fragment key={index}>
                            {handleTableDataStyle(value)}
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                  </td>
                ))}
              </tr>
            )}
            {["doubleRow", "tripleRow"].includes(row) &&
            secondRow &&
            secondRow.params[rowIndex]?.data &&
            secondRow.params[rowIndex]?.customLinkContainer
              ? secondRow.params[rowIndex]?.customLinkContainer
                  ?.hasDoubleRow && (
                  <tr
                    style={
                      secondRow.params[rowIndex]?.customLinkContainer
                        ?.trCSSProperties
                    }
                    className="custom-second-row"
                  >
                    <td colSpan={tableBody[0].body.length + 3}>
                      {secondRow.params[rowIndex]?.data.skeleton ? (
                        <div
                          style={
                            secondRow.params[rowIndex]?.data.skeleton
                              ?.containerCss
                          }
                        >
                          {secondRow.params[
                            rowIndex
                          ]?.data.skeleton?.values.map((skeletonProps, idx) => (
                            <Skeleton key={idx} {...skeletonProps} />
                          ))}
                        </div>
                      ) : (
                        handleTableDataStyle(secondRow.params[rowIndex]?.data)
                      )}
                    </td>
                    {secondRow.params[rowIndex]?.customLinkContainer
                      ?.hasDoubleRow && (
                      <td
                        key={rowIndex}
                        id={"link-container-" + rowIndex}
                        colSpan={tableBody[0].body.length + 3}
                      >
                        {secondRow.params[rowIndex]?.customLinkContainer?.node}
                      </td>
                    )}
                  </tr>
                )
              : ["doubleRow", "tripleRow"].includes(row) &&
                secondRow &&
                secondRow.params[rowIndex]?.data && (
                  <tr className="second-row">
                    <td colSpan={tableBody[0].body.length + 3}>
                      {secondRow.params[rowIndex]?.data.skeleton ? (
                        <div
                          style={
                            secondRow.params[rowIndex]?.data.skeleton
                              ?.containerCss
                          }
                        >
                          {secondRow.params[
                            rowIndex
                          ]?.data.skeleton?.values.map((skeletonProps, idx) => (
                            <Skeleton key={idx} {...skeletonProps} />
                          ))}
                        </div>
                      ) : (
                        <>
                          {handleTableDataStyle(
                            secondRow.params[rowIndex]?.data
                          )}
                          {secondRow.params[rowIndex]?.leftSideCustomNode}
                        </>
                      )}
                    </td>
                    <td
                      key={rowIndex}
                      className="link-container"
                      id={"link-container-" + rowIndex}
                      colSpan={tableBody[0].body.length + 3}
                    >
                      {secondRow.params[rowIndex]?.rightSideCustomNode}
                    </td>
                  </tr>
                )}
            {["tripleRow"].includes(row) &&
            thirdRow &&
            thirdRow.params[rowIndex]?.data &&
            thirdRow.params[rowIndex]?.customLinkContainer
              ? thirdRow.params[rowIndex].customLinkContainer?.hasDoubleRow && (
                  <tr
                    style={
                      thirdRow.params[rowIndex]?.customLinkContainer
                        ?.trCSSProperties
                    }
                    className="custom-second-row"
                  >
                    <td colSpan={tableBody[0].body.length + 3}>
                      {handleTableDataStyle(thirdRow.params[rowIndex].data)}{" "}
                    </td>
                    {thirdRow.params[rowIndex].customLinkContainer
                      ?.hasDoubleRow && (
                      <td
                        key={rowIndex}
                        id={"link-container-" + rowIndex}
                        colSpan={tableBody[0].body.length + 3}
                      >
                        {thirdRow.params[rowIndex]?.customLinkContainer?.node}
                      </td>
                    )}
                  </tr>
                )
              : ["tripleRow"].includes(row) &&
                thirdRow &&
                thirdRow.params[rowIndex]?.data && (
                  <tr className="second-row">
                    <td colSpan={tableBody[0].body.length + 3}>
                      {handleTableDataStyle(thirdRow.params[rowIndex].data)}
                      {thirdRow.params[rowIndex].leftSideCustomNode}
                    </td>

                    <td
                      key={rowIndex}
                      className="link-container"
                      id={"link-container-" + rowIndex}
                      colSpan={tableBody[0].body.length + 3}
                    >
                      {thirdRow.params[rowIndex].rightSideCustomNode}
                    </td>
                  </tr>
                )}
          </Fragment>
        );
      })}
    </Container>
  );
};

export default TBody;
